import { AfterViewInit, Component, ElementRef, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { MenuEvent, MenuSelectEvent } from '@progress/kendo-angular-menu';
import { filter, map } from 'rxjs/operators';
import { datePipeLocale } from 'src/app/shared/constants';

import { environment } from '../../environments/environment';
import { SESSION_INSTANCE } from '../msal/constants';
import { MsalService } from '../msal/msal.service';
import { AccessInfo } from '../shared/shared-access-module/openapi';
import { AccessControlService } from '../shared/shared-access-module/services/access-control.service';
import {
  DrawerConfig,
  MenuItem,
  ModalAction,
  SubMenuItem,
  UserMenuItem,
} from '../shared/shared-interfaces/shared-interfaces';
import {
  SessionInfo,
  SessionService,
} from '../shared/shared-services/session/session.service';
import { MenuService } from './services/menu/menu.service';
import { FeatureFlagService } from '../shared/shared-services/feature-flag/feature-flag.service';
import { combineLatest } from 'rxjs';
import { uniq } from 'lodash';
import { ScheduledJobOrchestratorService } from '../components/scheduler/services/scheduled-job-orchestrator.service';
import { ScheduledJobType } from '../components/scheduler/shared-scheduler-components/interfaces/scheduled-job.interface';

@Component({
  selector: 'prism-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements AfterViewInit, OnInit {
  @ViewChildren('subMenu') subMenus: QueryList<ElementRef>;

  public drawerConfig: DrawerConfig = {
    mode: 'push',
    isExpanded: true,
    autoCollapse: false,
  };
  public menuItems: MenuItem[];
  public userMenuItems: UserMenuItem[];
  public breadcrumbs: string;
  public currentYear = Date.now();
  public privacyPolicyURL = environment.privacyPolicyURL;
  public termsURL = environment.termsURL;
  public subMenuOpen = false;
  public selectedMenu: MenuItem;
  public fullName: string;
  public userTypeCode: string;
  public iconsMap: { [key: string]: string } = {
    userHeader: 'k-i-arrow-chevron-down',
    settings: 'k-i-cogs',
    notifications: 'k-i-info',
    Logout: 'k-i-logout',
    Administration: 'k-i-gear',
    'Patient Management': 'k-i-zoom',
    'Member Management': 'k-i-user',
    'Payers Management': 'k-i-paste-plain-text',
    'Order Management': 'k-i-copy',
  };
  public classMap: { [key: string]: string } = {
    userFullName: 'user-full-name',
  };
  public dateLocalePipe = datePipeLocale;

  public showMaintenanceDialog = false;
  public timeUntilRedirect: string;
  public maintenancePopupActions: ModalAction[] = [
    {
      text: 'OK',
      type: 'primary',
      callback: (_) => { this.showMaintenanceDialog = false; }
    }
  ];

  public openMenuIndex: number = -1;

  readonly MAX_ITEMS_PER_COLUMN = 7;

  public scheduledJob$ = this.scheduledJobOrchestratorService.activeScheduledJob$;
  public readonly ScheduledJobType = ScheduledJobType;

  constructor(
    private menuService: MenuService,
    private router: Router,
    private accessControlService: AccessControlService,
    private authService: MsalService,
    private featureFlagService: FeatureFlagService,
    @Inject(SESSION_INSTANCE) private readonly sessionService: SessionService,
    private readonly scheduledJobOrchestratorService: ScheduledJobOrchestratorService
  ) { }

  ngAfterViewInit(): void {
    this.subMenus.changes.subscribe(() => {
      if (this.openMenuIndex !== null) {
        const subMenuEl = this.subMenus.toArray()[0]?.nativeElement;
        if (subMenuEl) {
          this.adjustSubMenuPosition(subMenuEl);
        }
      }
    });
  }

  ngOnInit(): void {
    this.featureFlagService.onChanges().pipe(filter(changes => {
      return changes.flagKey === 'platform-maintenance' && changes.current;
    })).subscribe(_ => {
      this.showMaintenanceDialog = true;
      this.timeUntilRedirect = window.integra.maintenance.timeText;
      setTimeout(() => {
        this.router.navigate(['/maintenance']);
      }, window.integra.maintenance.timeout);
    });
    this.sessionService.sessionInfo
      .pipe(filter(Boolean))
      .subscribe((sessionInfo: SessionInfo) => {
        this.userTypeCode = sessionInfo.userTypeCode;
        this.fullName =
          sessionInfo.givenName.toUpperCase() +
          ' ' +
          sessionInfo.familyName.toUpperCase();
      });
    this.getMenuItems();
    this.getUserMenuItems();
  }

  getMenuItems(): void {
    const menuItems = this.menuService.getMenuItems();
    const flags = this.collectFeatureFlags(menuItems);

    if (flags.length > 0) {
      const flagOBs = flags.map(flag =>
        this.featureFlagService.getFlag(flag).pipe(
          map(value => ({ flag, value }))
        )
      );
      combineLatest(flagOBs)
        .pipe(map(values => values.filter(val => !val.value).map(val => val.flag)))
        .subscribe(hiddenFlags => {
          const filtered = this.filterMenuItemsByFlags(menuItems, hiddenFlags);
          this.getAccessForMenuItems(filtered);
        });
    } else {
      this.getAccessForMenuItems(menuItems);
    }
  }

  private collectFeatureFlags(menuItems: MenuItem[]): string[] {
    return uniq([
      ...menuItems.filter(item => !!item.featureFlag).map(item => item.featureFlag),
      ...([]
        .concat(
          ...menuItems.map(item =>
            (item.items ?? []).map(subMenu =>
              (subMenu.items ?? []).filter(subItem => !!subItem.featureFlag).map(subItem => subItem.featureFlag)
            )
          )
        )
        .reduce((acc, val) => acc.concat(val), [])
        .filter(Boolean)
      )
    ]);
  }

  private filterMenuItemsByFlags(menuItems: MenuItem[], hiddenFlags: string[]): MenuItem[] {
    return menuItems
      .filter(item => this.isHiddenByFlag(item, hiddenFlags))
      .map(item => {
        if (item.items?.length) {
          item.items = item.items
            .map(subMenu => ({
              ...subMenu,
              items: subMenu.items.filter(child => this.isHiddenByFlag(child, hiddenFlags))
            }))
            .filter(subMenu => subMenu.items.length > 0);
        }
        return item;
      });
  }

  private isHiddenByFlag(menuItem: MenuItem, featureFlags: string[]): boolean {
    return !menuItem.featureFlag || !featureFlags.includes(menuItem.featureFlag);
  }

  private getAccessForMenuItems(menuItems: MenuItem[]): void {
    for (let idx = menuItems.length - 1; idx >= 0; idx--) {
      this.accessControlService.addPermission([menuItems[idx]], true);
      if (menuItems[idx].items) {
        this.processSubMenus(menuItems[idx].items);
        menuItems[idx].items = menuItems[idx].items.filter(subMenu => subMenu.items.length > 0);
      }
    }
    this.menuItems = menuItems.filter(item =>
      (item.items && item.items.some(subMenu => subMenu.items.length > 0)) ||
      item.actions?.includes(AccessInfo.ActionsEnum.Enabled)
    ).map(item => {
      item.collapsedItem = [{
        text: item.text,
        header: true,
        items: this.getCollapsedMenuItem(item)
      }];
      return {
        ...item,
        columns: this.groupCategoriesIntoColumns(item)
      };
    });
  }

  private processSubMenus(subMenus: any[]): void {
    subMenus.forEach(subMenu => {
      this.accessControlService.addPermission(subMenu.items, true);
      subMenu.items = subMenu.items.filter(item =>
        item.actions?.includes(AccessInfo.ActionsEnum.Enabled)
      );
    });
  }

  getUserMenuItems(): void {
    this.userMenuItems = this.menuService.getUserMenuItems(this.fullName);
  }

  iconClass({ text }: UserMenuItem): string | undefined {
    return this.iconsMap[text];
  }

  textClass(text: string): string | undefined {
    return this.classMap[text];
  }

  onSelect(selected: MenuItem): void {
    selected.expanded = !selected.expanded;
    if (!!selected.path) {
      this.router.navigate([selected.path]);
    }
  }

  onSelectCollapsed($event: MenuEvent): void {
    if (!!$event.item.path) {
      this.router.navigate([$event.item.path]);
    }
  }

  getCollapsedMenuItem(item: MenuItem): UserMenuItem[] {
    const collapsedItems: UserMenuItem[] = [];
    item.items?.forEach(subMenu => {
      subMenu.items?.forEach(subItem => {
        collapsedItems.push({ text: subItem.text });
      });
    });
    return collapsedItems;
  }

  selectUserMenuItem($event: MenuSelectEvent): void {
    if ($event.item.action === 'logoutFromApp') {
      this.authService.logout();
    }
  }

  toggleSubMenu(index: number): void {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  groupCategoriesIntoColumns(menuItem: MenuItem): SubMenuItem[][] {
    const categories = menuItem?.items || [];
    const columns: any[][] = [];
    let currentColumn: any[] = [];
    let currentCount = 0;

    for (const cat of categories) {
      const catLength = cat.items?.length || 0;

      if (
        currentColumn.length > 0 &&
        (currentCount + catLength > this.MAX_ITEMS_PER_COLUMN)
      ) {
        columns.push(currentColumn);
        currentColumn = [];
        currentCount = 0;
      }
      currentColumn.push(cat);
      currentCount += catLength;
    }
    if (currentColumn.length > 0) {
      columns.push(currentColumn);
    }
    return columns;
  }

  private adjustSubMenuPosition(subMenuElement: HTMLElement): void {
    const rect = subMenuElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    if (rect.right > viewportWidth) {
      const overflow = rect.right - viewportWidth + 16;
      subMenuElement.style.left = `-${overflow}px`;
    } else {
      subMenuElement.style.left = '0px';
    }
  }

  public closeScheduleJobModal(event: any = {}): void {
    this.scheduledJobOrchestratorService.stop(event.refresh ?? false);
  }
}
